// Tab-scoped, single-use store for the route an unauthenticated user was trying to reach.
// sessionStorage (not localStorage) is deliberate: it survives the full page reload that the
// 401 path does (window.location.replace("/")) and the Auth0 round-trip (which returns the
// browser to window.location.origin, discarding any query param), it's scoped to one tab, and
// it's untouched by the several localStorage.clear() calls in the auth teardown paths.
//
// Invariant: resolve-once-per-page-load, single-use across page loads. There can be more than
// one post-login navigator alive on the same page load (e.g. LoginV2/Login/Login.jsx's own
// handlePostLoginNavigation effect runs alongside GoogleSignup.jsx/MicrosoftSignup.jsx's own
// post-auth effects, both watching redux state that updates more than once), so the FIRST read
// of sessionStorage within a page load removes the entry, but every subsequent read in that same
// page load must keep returning the identical value — otherwise the first navigator to run wins
// the deep link and every later one silently gets bounced to the fallback. The removal from
// sessionStorage still happens on that first read, so a genuinely new page load (the only way
// this app reaches a second login — see saveIntendedRoute/consumeIntendedRoute call sites and
// the logout/401/Auth0 paths, which all force a full reload) starts with nothing memoized.
import {
  LOGIN_ROUTE,
  SSO_ROUTE,
  LINK_EXPIRED_ROUTE,
  RESET_PASSWORD_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  ADD_CUSTOMER_INFO_ROUTE,
  RESEND_LINK_ROUTE,
  EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE,
  PLG_SIGNUP_ROUTE,
} from "constants/routes";

const STORAGE_KEY = "postLoginIntendedRoute";
const CAMPAIGN_STORAGE_KEY = "postLoginCampaignParams";

// Exact public/auth paths — mirrors the public entries at the top of useRoutes([...]) in
// src/routes/index.jsx. A route in here must never be saved as the "intended" destination,
// otherwise login would just bounce back into itself.
const PUBLIC_ROUTES = new Set([
  LOGIN_ROUTE,
  "/admin",
  "/employee",
  "/changePassword",
  LINK_EXPIRED_ROUTE,
  "/leaveStatus",
  "/addPaymentMethod",
  RESEND_LINK_ROUTE,
  EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE,
  "/terms",
  SSO_ROUTE,
  "/policy",
  "/employee-policy",
  FORGOT_PASSWORD_ROUTE,
  RESET_PASSWORD_ROUTE,
  ADD_CUSTOMER_INFO_ROUTE,
  PLG_SIGNUP_ROUTE,
]);

// Prefix-matched public route groups (dynamic segments, e.g. the PLG sign-up steps "/signup/:stepKey").
const PUBLIC_ROUTE_PREFIXES = ["/signup/", "/customerOnboarding/", "/marketplace/public"];

const isPublicRoute = (route: string): boolean => {
  const [pathname] = route.split(/[?#]/);
  return PUBLIC_ROUTES.has(pathname) || PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
};

// Open-redirect guard: accept only a same-origin relative path. Used both for what we store
// ourselves and for the "next" param the cross-portal SSO handoff receives from another origin.
export const isSafeInternalRoute = (route: unknown): boolean => {
  if (typeof route !== "string" || route.length === 0) {
    return false;
  }
  if (route[0] !== "/") {
    return false;
  }
  if (route.startsWith("//") || route.startsWith("/\\")) {
    return false;
  }
  return true;
};

export const saveIntendedRoute = (route: string): void => {
  if (!isSafeInternalRoute(route) || isPublicRoute(route)) {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, route);
  } catch {
    // Safari private mode (and similar) throws on sessionStorage access — never let that break login.
  }
};

// Memoized after the first resolution within a page load — see the module comment above. `null`
// is a valid memoized outcome (no route was saved), which is why "has this resolved yet" is
// tracked separately from the value itself rather than checking `!== undefined`.
let intendedRouteMemo: { resolved: boolean; value: string | null } = { resolved: false, value: null };

export const consumeIntendedRoute = (): string | null => {
  if (!intendedRouteMemo.resolved) {
    let value: string | null = null;
    try {
      const route = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      value = route && isSafeInternalRoute(route) ? route : null;
    } catch {
      value = null;
    }
    intendedRouteMemo = { resolved: true, value };
  }
  return intendedRouteMemo.value;
};

// First-touch campaign attribution: capture any utm_* params seen on the tab's landing URL so
// they can be re-attached to wherever the user actually lands post-login, even when that landing
// URL is a public/auth screen that saveIntendedRoute deliberately never stores. First touch wins —
// a later call with different values must not clobber what the landing URL already recorded.
export const captureCampaignParams = (search: string): void => {
  try {
    if (sessionStorage.getItem(CAMPAIGN_STORAGE_KEY)) {
      return;
    }

    const utmParams: Record<string, string> = {};
    new URLSearchParams(search).forEach((value, key) => {
      if (key.startsWith("utm_")) {
        utmParams[key] = value;
      }
    });

    if (Object.keys(utmParams).length === 0) {
      return;
    }

    sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(utmParams));
  } catch {
    // Safari private mode (and similar) throws on sessionStorage access — never let that break boot.
  }
};

// Memoized after the first resolution within a page load — same reasoning as intendedRouteMemo
// above: multiple post-login navigators may each call withCampaignParams during one page load,
// and all of them must merge the same params, not just whichever runs first.
let campaignParamsMemo: { resolved: boolean; value: Record<string, string> | null } = {
  resolved: false,
  value: null,
};

const resolveCampaignParams = (): Record<string, string> | null => {
  if (!campaignParamsMemo.resolved) {
    let value: Record<string, string> | null = null;
    try {
      const stored = sessionStorage.getItem(CAMPAIGN_STORAGE_KEY);
      sessionStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      value = stored ? JSON.parse(stored) : null;
    } catch {
      value = null;
    }
    campaignParamsMemo = { resolved: true, value };
  }
  return campaignParamsMemo.value;
};

// Attaches the memoized campaign params onto `route`'s query string without overwriting a param
// `route` already carries. Route is a relative path, so it's parsed by hand rather than via
// `new URL()`, which requires an absolute base.
export const withCampaignParams = (route: string): string => {
  try {
    const utmParams = resolveCampaignParams();
    if (!utmParams) {
      return route;
    }

    const hashIndex = route.indexOf("#");
    const hash = hashIndex === -1 ? "" : route.slice(hashIndex);
    const beforeHash = hashIndex === -1 ? route : route.slice(0, hashIndex);

    const queryIndex = beforeHash.indexOf("?");
    const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
    const existingSearch = queryIndex === -1 ? "" : beforeHash.slice(queryIndex + 1);

    const mergedParams = new URLSearchParams(existingSearch);
    Object.entries(utmParams).forEach(([key, value]) => {
      if (!mergedParams.has(key)) {
        mergedParams.set(key, value);
      }
    });

    const mergedSearch = mergedParams.toString();
    return `${path}${mergedSearch ? `?${mergedSearch}` : ""}${hash}`;
  } catch {
    return route;
  }
};

// The standard "land the user" resolution for a login screen that navigates directly rather
// than going through handlePostLoginNavigation (e.g. Google/Microsoft sign-up's post-auth
// effects): prefer the saved deep link over `fallback`, then attach any campaign params.
export const resolvePostLoginRoute = (fallback: string): string =>
  withCampaignParams(consumeIntendedRoute() ?? fallback);

// Test-only: clears the page-load memoization so each test starts from a clean slate, modeling
// a fresh page load. Not used, and must never be used, from application code.
export const __resetPostLoginRedirectMemoForTests = (): void => {
  intendedRouteMemo = { resolved: false, value: null };
  campaignParamsMemo = { resolved: false, value: null };
};
